// src/components/parent-dashboard/components/CaregiverObservation.js
import React from "react";
import {isPreparatoryGrade} from "../utils/gradeUtils";

/**
 * Caregiver Observation Component (for Parent Dashboard)
 * Allows parents to provide feedback on their child's work
 * Works for both foundational and preparatory stages
 */
const CaregiverObservation = ({
                                  observationForm,
                                  handleResourceChange,
                                  handleCommentsChange,
                                  handleObservationSubmit,
                                  selectedSubmission,
                                  setObservationForm,
                                  selectedClass, // Class info for grade level
                                  isPreparatory // Direct flag
                              }) => {
    // Use isPreparatory prop if provided, otherwise calculate it
    let isPrepStage = isPreparatory;

    // Only calculate if not provided directly
    if (isPrepStage === undefined) {
        const grade = selectedClass?.grade ? parseInt(selectedClass.grade, 10) : 1;
        isPrepStage = isPreparatoryGrade(grade);
    }

    // For debugging - remove in production
    console.log("CaregiverObservation - isPrepStage:", isPrepStage, "grade:", selectedClass?.grade);

    // If already submitted, show the submitted observation in the new UI
    if (selectedSubmission && selectedSubmission.caregiver_observation) {
        const resources = selectedSubmission.caregiver_observation.resources || [];

        return (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h2 className="text-lg leading-6 font-medium text-gray-900">Caregiver Observation</h2>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        Your observations about your child's work
                    </p>
                </div>
                <div className="border-t border-gray-200">
                    <div className="overflow-hidden">
                        <table className="min-w-full divide-y divide-orange-200 border-collapse">
                            <thead>
                            <tr className="bg-orange-100">
                                <th scope="col"
                                    className="px-6 py-3 text-left text-base font-medium text-orange-800 w-1/2">
                                    Parents/Caregiver/Guardian's Observation
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-base font-medium text-orange-800 w-1/2">
                                    Comments/Remarks
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-orange-200">
                            <tr>
                                <td className="px-0 py-4 align-top border border-orange-200">
                                    <div className="p-2">
                                        <div className="text-gray-700 mb-3">Selected resources:</div>
                                        <div
                                            className="flex flex-row items-stretch h-36 border border-gray-200 rounded-lg overflow-hidden">
                                            {/* Left blue sidebar */}
                                            <div
                                                className="bg-blue-600 text-white p-3 flex flex-col justify-center items-center text-center w-1/4">
                                                <span className="text-sm font-medium">Learning</span>
                                                <span className="text-sm font-medium">Teaching</span>
                                                <span className="text-sm font-medium">resources</span>
                                                <span className="text-sm font-medium">at</span>
                                                <span className="text-sm font-medium">home</span>
                                            </div>

                                            {/* Resources selection area */}
                                            <div className="flex-1 bg-blue-50 flex items-center justify-around px-2">
                                                <div className="flex flex-col items-center">
                                                    <div
                                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border ${resources.includes("books/magazine") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50'}`}>
                                                        <span role="img" aria-label="books"
                                                              className="text-orange-500">📚</span>
                                                    </div>
                                                    <span
                                                        className="mt-1 text-xs text-gray-600 text-center">books/magazine</span>
                                                </div>

                                                <div className="h-full flex items-center">
                                                    <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div
                                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border ${resources.includes("newspaper") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50'}`}>
                                                        <span role="img" aria-label="newspaper"
                                                              className="text-orange-500">📰</span>
                                                    </div>
                                                    <span
                                                        className="mt-1 text-xs text-gray-600 text-center">newspaper</span>
                                                </div>

                                                <div className="h-full flex items-center">
                                                    <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div
                                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border ${resources.includes("toys/games/sports") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50'}`}>
                                                        <span role="img" aria-label="toys"
                                                              className="text-orange-500">🧸</span>
                                                    </div>
                                                    <span
                                                        className="mt-1 text-xs text-gray-600 text-center">toys/games/sports</span>
                                                </div>

                                                <div className="h-full flex items-center">
                                                    <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div
                                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border ${resources.includes("phone/computer") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50'}`}>
                                                        <span role="img" aria-label="computer"
                                                              className="text-orange-500">💻</span>
                                                    </div>
                                                    <span
                                                        className="mt-1 text-xs text-gray-600 text-center">phone/computer</span>
                                                </div>

                                                <div className="h-full flex items-center">
                                                    <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div
                                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border ${resources.includes("internet") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50'}`}>
                                                        <span role="img" aria-label="internet"
                                                              className="text-orange-500">🌐</span>
                                                    </div>
                                                    <span
                                                        className="mt-1 text-xs text-gray-600 text-center">internet</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-800 border border-orange-200">
                                    <div className="h-full p-2 min-h-[150px]">
                                        {selectedSubmission.caregiver_observation.comments || "No additional comments provided."}
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Edit button */}
                    <div className="px-4 py-3 bg-gray-50 text-right">
                        <button
                            type="button"
                            onClick={() => {
                                setObservationForm({
                                    resources: selectedSubmission.caregiver_observation.resources || [],
                                    comments: selectedSubmission.caregiver_observation.comments || ""
                                });
                            }}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Edit Observations
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Form for submitting caregiver observation
    return (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6 bg-gray-50">
                <h2 className="text-lg leading-6 font-medium text-gray-900">
                    {isPrepStage ? "Caregiver Detailed Observation" : "Caregiver Observation"}
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Share your observations about your child's work on this assignment
                </p>
            </div>

            <div className="border-t border-gray-200">
                {!selectedSubmission ? (
                    <div className="px-4 py-5 sm:p-6 text-center">
                        <div className="h-10 w-10 text-gray-400 mx-auto mb-3">📝</div>
                        <p className="text-sm text-gray-500">
                            You can add your observations once your child submits this assignment.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleObservationSubmit}>
                        <div className="overflow-hidden">
                            <table className="min-w-full divide-y divide-orange-200 border-collapse">
                                <thead>
                                <tr className="bg-orange-100">
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-base font-medium text-orange-800 w-1/2">
                                        Parents/Caregiver/Guardian's Observation
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-base font-medium text-orange-800 w-1/2">
                                        Comments/Remarks
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-orange-200">
                                <tr>
                                    <td className="px-0 py-4 align-top border border-orange-200">
                                        <div className="p-2">
                                            <div className="text-gray-700 mb-3">
                                                {isPrepStage
                                                    ? "Select the learning resources your child used:"
                                                    : "Circle the relevant response:"}
                                            </div>
                                            <div
                                                className="flex flex-row items-stretch h-36 border border-gray-200 rounded-lg overflow-hidden">
                                                {/* Left blue sidebar */}
                                                <div
                                                    className="bg-blue-600 text-white p-3 flex flex-col justify-center items-center text-center w-1/4">
                                                    <span className="text-sm font-medium">Learning</span>
                                                    <span className="text-sm font-medium">Teaching</span>
                                                    <span className="text-sm font-medium">resources</span>
                                                    <span className="text-sm font-medium">at</span>
                                                    <span className="text-sm font-medium">home</span>
                                                </div>

                                                {/* Resources selection area */}
                                                <div
                                                    className="flex-1 bg-blue-50 flex items-center justify-around px-2">
                                                    <div className="flex flex-col items-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleResourceChange("books/magazine")}
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border focus:outline-none ${(observationForm.resources || []).includes("books/magazine") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50 hover:bg-blue-100'}`}
                                                        >
                                                            <span role="img" aria-label="books"
                                                                  className="text-orange-500">📚</span>
                                                        </button>
                                                        <span
                                                            className="mt-1 text-xs text-gray-600 text-center">books/magazine</span>
                                                    </div>

                                                    <div className="h-full flex items-center">
                                                        <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleResourceChange("newspaper")}
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border focus:outline-none ${(observationForm.resources || []).includes("newspaper") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50 hover:bg-blue-100'}`}
                                                        >
                                                            <span role="img" aria-label="newspaper"
                                                                  className="text-orange-500">📰</span>
                                                        </button>
                                                        <span
                                                            className="mt-1 text-xs text-gray-600 text-center">newspaper</span>
                                                    </div>

                                                    <div className="h-full flex items-center">
                                                        <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleResourceChange("toys/games/sports")}
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border focus:outline-none ${(observationForm.resources || []).includes("toys/games/sports") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50 hover:bg-blue-100'}`}
                                                        >
                                                            <span role="img" aria-label="toys"
                                                                  className="text-orange-500">🧸</span>
                                                        </button>
                                                        <span
                                                            className="mt-1 text-xs text-gray-600 text-center">toys/games/sports</span>
                                                    </div>

                                                    <div className="h-full flex items-center">
                                                        <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleResourceChange("phone/computer")}
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border focus:outline-none ${(observationForm.resources || []).includes("phone/computer") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50 hover:bg-blue-100'}`}
                                                        >
                                                            <span role="img" aria-label="computer"
                                                                  className="text-orange-500">💻</span>
                                                        </button>
                                                        <span
                                                            className="mt-1 text-xs text-gray-600 text-center">phone/computer</span>
                                                    </div>

                                                    <div className="h-full flex items-center">
                                                        <div className="h-full w-px bg-gray-300 mx-1"></div>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleResourceChange("internet")}
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border focus:outline-none ${(observationForm.resources || []).includes("internet") ? 'border-orange-500 bg-white' : 'border-gray-300 bg-blue-50 hover:bg-blue-100'}`}
                                                        >
                                                            <span role="img" aria-label="internet"
                                                                  className="text-orange-500">🌐</span>
                                                        </button>
                                                        <span
                                                            className="mt-1 text-xs text-gray-600 text-center">internet</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500 border border-orange-200">
                                      <textarea
                                          id="comments"
                                          name="comments"
                                          rows={6}
                                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                          placeholder={isPrepStage
                                              ? "Share detailed observations about your child's learning approach, challenges faced, and progress noticed..."
                                              : "Share any observations about how your child approached this assignment..."}
                                          value={observationForm.comments || ""}
                                          onChange={handleCommentsChange}
                                      />
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Submit button */}
                        <div className="px-4 py-3 bg-gray-50 text-right">
                            <button
                                type="submit"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                            >
                                Submit Observations
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CaregiverObservation;